import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';

import { useCapability } from '@vimeo/core/hooks/use-capability';
import { ContextOverrides } from '@vimeo/eppo';
import { translate } from '@vimeo/i18n-runtime';
import { UpgradeBadge } from '@vimeo/monetization-upsells';
import { buildUpgradePlanUrl } from '@vimeo/picox';
import { getPageNameFromPath } from '@vimeo/ui/core/F2PJoinLogin/utils';
import getDeviceType from '@vimeo/ui/core/utils/getDeviceType';
import { useUpsellModalFlag } from '@vimeo/ui/hooks/use-upsell-modal-flag';
import { WebBpContext } from '@vimeo/use-tracking';
import { Viewer } from '@vimeo/viewer';

import { trackNavigationActionEvent, trackNavigationViewEvent } from '../../tracking';

const UpsellModal = dynamic(() => import('@vimeo/upsell-modal'));

interface Props {
  viewer: Viewer | null;
}

export const Upgrade = ({ viewer }: Props) => {
  const user = viewer && viewer.user;
  const teamUser = viewer && viewer.teamUser;
  const hasTrackedImpression = useRef(false);
  const { getShouldShowUpsellModal } = useUpsellModalFlag();
  const [showUpsellModal, setShowUpsellModal] = useState(false);

  const {
    capabilities: { hasEnterprise: ownerHasEnterprise },
    loading: loadingOwnerCapability,
  } = useCapability(['hasEnterprise'], teamUser?.ownerId);

  const {
    capabilities: { canUpgrade },
    loading: loadingCapability,
  } = useCapability(['canUpgrade'], user?.id);

  const shouldShowUpgradeCTA =
    viewer &&
    !(loadingCapability || loadingOwnerCapability) &&
    canUpgrade &&
    !ownerHasEnterprise &&
    !viewer.isSimplifiedSite;

  useEffect(() => {
    if (viewer && shouldShowUpgradeCTA && !hasTrackedImpression.current) {
      hasTrackedImpression.current = true;
      trackNavigationViewEvent({
        copy: 'upgrade',
        element: 'button',
        eventName: 'vimeo.upsell_trigger_impression',
        additionalFields: { upsell_name: 'top_nav_bar_upgrade' },
        viewer,
        version: 5,
      });
    }
  }, [shouldShowUpgradeCTA, viewer]);

  if (!shouldShowUpgradeCTA) {
    return <></>;
  }

  const getBigPictureOverrides = () => {
    let pageName: WebBpContext['page_name'] = 'other';
    let path: WebBpContext['path'] | null = null;
    let referrerPageName: WebBpContext['referrer_page_name'] | null = null;
    let referrer: WebBpContext['referrer'] | null = null;

    if (typeof window !== 'undefined' && window.location?.pathname) {
      path = window.location.pathname as WebBpContext['path'];
      pageName = getPageNameFromPath(window.location.pathname, user) as WebBpContext['page_name'];
    }
    if (typeof document !== 'undefined' && document.referrer) {
      referrer = new URL(document.referrer).pathname as WebBpContext['referrer'];
      referrerPageName = getPageNameFromPath(
        new URL(document.referrer).pathname,
        user,
      ) as WebBpContext['referrer_page_name'];
    }

    const bigPictureOverrides: ContextOverrides = {
      product_analytics_context: {
        device_type: getDeviceType(),
      },
      web_context: {
        path,
        page_name: pageName,
        referrer,
        referrer_page_name: referrerPageName,
      },
    };

    return bigPictureOverrides;
  };

  return (
    <div>
      <UpgradeBadge
        noMargin
        name="top_nav_upgrade_button"
        location="top_navigation"
        onClick={() => {
          const shouldShowUpsellModal = getShouldShowUpsellModal(
            'top_nav_bar_upgrade',
            'top-nav-upgrade-badge',
            getBigPictureOverrides(),
            { location: viewer?.location || 'unknown' },
          );

          shouldShowUpsellModal
            ? setShowUpsellModal(true)
            : (window.location.href = buildUpgradePlanUrl(
                {
                  paywallTrigger: 'top_navigation_upgrade_button',
                  paywallLocation: 'top_navigation',
                  paywallFeature: 'general',
                },
                {
                  upsell: 'top_nav_bar_upgrade',
                  integration: 'none',
                  feature: 'general',
                  mkc: 'global_top_nav',
                },
              ));

          trackNavigationActionEvent({
            copy: 'upgrade',
            eventName: 'vimeo.trigger_upsell',
            element: 'button',
            viewer,
            version: 7,
            additionalFields: {
              upsell_name: 'top_nav_bar_upgrade',
            },
          });
          trackNavigationActionEvent({
            copy: 'upgrade',
            element: 'button',
            feature: 'pricing',
            viewer,
            version: 7,
            additionalFields: {
              upsell_name: 'top_nav_bar_upgrade',
            },
          });
        }}
      />

      {showUpsellModal && (
        <UpsellModal
          apiUrl={viewer?.apiUrl}
          userConfig={{ jwt: viewer?.jwt, userId: viewer?.user?.id }}
          templateType="default"
          onClose={() => setShowUpsellModal(false)}
          modalConfig={{
            hidePlansSummary: true,
            hasFreeFeaturedBanner: true,
            headerText: '',
            subHeaderText: '',
            featuredBannerText: translate({
              singular: 'Upgrade to unlock more features',
            }),
          }}
          tracking={{
            params: {
              feature: 'pricing',
            },
            paywallTracking: {
              paywallTrigger: 'top_navigation_upgrade_button',
              paywallLocation: 'top_navigation',
              paywallType: 'popup',
              paywallFeature: 'general',
            },
          }}
        />
      )}
    </div>
  );
};
